import React, { useCallback, useMemo, useRef, useState } from 'react';
import cn from 'clsx';
import { css } from 'linaria';
import {
  ButtonWithGAClickButton,
  ModalMediaCardBody,
  ModalMediaCardFooter,
  ModalMediaCardGallery,
  ModalMediaCardImage,
  MoreInfoCard,
  MoreInfoCardBody,
  MoreInfoCardGrid,
  MoreInfoCardImage,
  Section,
  SectionSubTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
  Typography,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { WINDOWS_ELEMENT_BLOCK } from '../../windows-constants';
import { WINDOWS_PROJECT_TYPES_LIST, WINDOWS_PROJESTS_SELECTION_GROUP } from './windows-projects-constants';

type WindowsTypesSectionProps = {
  onConsultationSignupBtnClick: () => void;
};

const windowsProjectsCardImage = css`
  @media (--only-largeDesktop) {
    height: 320px;
  }

  @media (--only-mobile) {
    height: 216px;
  }
`;

const windowsProjectsOptions = css`
  margin-bottom: 24px;

  @media (--only-tablet) {
    margin-bottom: 32px;
  }
`;

const windowsProjectsGrid = css`
  && {
    @media (--from-tablet) {
      :last-child {
        grid-column: 1 / 3;
      }
    }
  }
`;

export const WindowsProjects = ({ onConsultationSignupBtnClick }: WindowsTypesSectionProps) => {
  const cardRefs = useRef<HTMLDivElement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeSelectionGroup, setActiveSelectionGroup] = useState(WINDOWS_PROJESTS_SELECTION_GROUP[0].value);
  const [activeCardIndex, setActiveCardIndex] = useState<number>(0);
  
  const windowsType = WINDOWS_PROJECT_TYPES_LIST[activeSelectionGroup];
  const isEvenCard = windowsType.length % 2 === 0;

  const promotions = useMemo(
    () =>
      windowsType.map(({ title }, index: number) => ({
        name: WINDOWS_ELEMENT_BLOCK,
        creative: title,
        position: (index + 1).toString(),
      })),
    [windowsType]
  );

  usePromoViewEvent(cardRefs, WINDOWS_ELEMENT_BLOCK, promotions);

  /**
   * Обработчик нажатия на карточку со стилями
   */
  const handleCardClick = useCallback(
    (index: number) => () => {
      setActiveCardIndex(index);
      setIsModalOpen(true);
      analyticsUtils.promoClickEvent(WINDOWS_ELEMENT_BLOCK, promotions[index]);
    },
    [promotions, setIsModalOpen, setActiveCardIndex]
  );

  const handleModalBtnClick = useCallback(() => {
    setIsModalOpen(false);
    setActiveCardIndex(0);
    onConsultationSignupBtnClick();
  }, [onConsultationSignupBtnClick]);

  /**
   * Обработчик закрытия модального окна
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleTabChange = (e: React.MouseEvent, value: SelectionGroupValue) => {
    if (value !== activeSelectionGroup) {
      setActiveSelectionGroup(value as string);

      const activeOption: SelectionGroupOption | undefined = WINDOWS_PROJESTS_SELECTION_GROUP.find(
        (option: SelectionGroupOption) => option.value === value
      );
      if (activeOption) {
        analyticsUtils.clickButtonEvent(
          WINDOWS_ELEMENT_BLOCK,
          activeOption.text,
          'Для каждого дома — своё остекление'
        );
      }
    }
  }

  const handleCardChange = useCallback((index: number) => {
    setActiveCardIndex(index);
  }, []);

  return (
    <Section>
      <SectionSubTitle margin="m">Для каждого дома — своё остекление</SectionSubTitle>
      <SelectionGroup
        active={activeSelectionGroup}
        className={windowsProjectsOptions}
        onChange={handleTabChange}
        optionList={WINDOWS_PROJESTS_SELECTION_GROUP}
        rounded
      />
      <MoreInfoCardGrid cardPerRow={2} className={cn({[windowsProjectsGrid]: !isEvenCard})}>
        {windowsType.map(({ imgSrc, title }, index: number) => (
          <MoreInfoCard
            data-position={index}
            key={createElementKey('windows-type', index)}
            onClick={handleCardClick(index)}
            ref={(el: HTMLDivElement | null) => {
              if (el && cardRefs) cardRefs.current[index] = el;
            }}>
            <MoreInfoCardImage alt={title} className={windowsProjectsCardImage} loading="lazy" src={imgSrc} />
            <MoreInfoCardBody title={title} />
          </MoreInfoCard>
        ))}
      </MoreInfoCardGrid>

      <ModalMediaCardGallery
        cardList={windowsType}
        elementBlock={WINDOWS_ELEMENT_BLOCK}
        isOpen={isModalOpen}
        openCardIndex={activeCardIndex}
        onCardChange={handleCardChange}
        onClose={handleCloseModal}
        renderContent={({ description, modalImgSrc, title }) => (
          <>
            <ModalMediaCardBody title={title}>
              <Typography adaptive={false} variant="m" size="body_long">
                {description}
              </Typography>

              <ModalMediaCardFooter>
                <ButtonWithGAClickButton
                  elementBlock={WINDOWS_ELEMENT_BLOCK}
                  elementName={title}
                  elementText={'Записаться на консультацию'}
                  onClick={handleModalBtnClick}
                  wide={false}
                >
                  Записаться на консультацию
                </ButtonWithGAClickButton>
              </ModalMediaCardFooter>
            </ModalMediaCardBody>
            <ModalMediaCardImage alt={title} src={modalImgSrc} title={title} />
          </>
        )}
      />
    </Section>
  );
};
